/**
 * QR code como SVG inline.
 *
 * POR QUE SVG INLINE, e não imagem:
 *
 *   - A política de segurança da página (`img-src 'self' data:`) aceitaria uma
 *     `data:` URI, mas SVG inline não precisa nem disso: são elementos do próprio
 *     documento. Nada é carregado, de lugar nenhum.
 *   - Fica nítido em qualquer tamanho e em qualquer densidade de tela. QR
 *     rasterizado num monitor comum produz módulo borrado, e módulo borrado é
 *     leitura que falha justamente quando a pessoa está de pé com o celular na mão.
 *   - Imprime bem. Alguém vai imprimir para cadastrar longe do computador.
 *
 * POR QUE O CONTEÚDO NUNCA SAI DAQUI: o QR carrega o segredo TOTP. Serviço externo
 * de geração de QR — daqueles que montam a imagem a partir da URL — receberia o
 * segundo fator de um administrador em texto claro, no log de acesso de terceiro.
 * Isso não é uma possibilidade a avaliar; é a razão de a geração ser local.
 *
 * A biblioteca (`qrcode-generator`, de Kazuhiko Arase) é a implementação de
 * referência em JavaScript, com ZERO dependências transitivas — e é usada aqui
 * apenas para calcular a matriz de módulos. O desenho é nosso.
 */

import { useMemo } from 'react';
import qrcode from 'qrcode-generator';

/**
 * Nível de correção de erro.
 *
 * `M` recupera ~15% da área danificada e é o que praticamente todo autenticador
 * assume. `L` gera um QR menor e mais frágil; `H` gera um denso demais, com módulo
 * pequeno na tela — e módulo pequeno é o que faz a câmera desistir.
 */
const CORRECAO = 'M';

export function QrCode({
  /** Conteúdo codificado. Aqui, sempre a URI `otpauth://`. */
  value,
  /** Lado do quadrado, em pixels CSS. */
  size = 208,
  /** Texto alternativo — o QR é imagem, e leitor de tela precisa saber o que é. */
  label,
  className = '',
}: {
  value: string;
  size?: number;
  label: string;
  className?: string;
}): JSX.Element | null {
  const matriz = useMemo(() => {
    try {
      // `0` = a biblioteca escolhe a menor versão que couber. Fixar a versão
      // faria a geração falhar no dia em que o e-mail de alguém fosse mais longo.
      const qr = qrcode(0, CORRECAO);
      qr.addData(value);
      qr.make();

      const modulos = qr.getModuleCount();
      const escuros: Array<[number, number]> = [];
      for (let linha = 0; linha < modulos; linha += 1) {
        for (let coluna = 0; coluna < modulos; coluna += 1) {
          if (qr.isDark(linha, coluna)) escuros.push([linha, coluna]);
        }
      }
      return { modulos, escuros };
    } catch {
      // Conteúdo que não cabe em QR versão 40 é o único caso de falha real. Em vez
      // de derrubar a tela de cadastro, devolve nada — e a chave manual, que fica
      // logo abaixo, continua funcionando.
      return null;
    }
  }, [value]);

  if (matriz === null) return null;

  const { modulos, escuros } = matriz;
  // Zona de silêncio de 4 módulos: exigência da especificação de QR. Sem ela,
  // câmera confunde a borda do código com o conteúdo da tela.
  const margem = 4;
  const lado = modulos + margem * 2;

  return (
    <svg
      // `viewBox` em unidades de MÓDULO, e não de pixel: o navegador escala sem
      // erro de arredondamento, e o código continua legível em qualquer tamanho.
      viewBox={`0 0 ${lado} ${lado}`}
      width={size}
      height={size}
      role="img"
      aria-label={label}
      className={`shrink-0 ${className}`}
      // `crispEdges` evita a suavização que borra a fronteira entre módulos —
      // exatamente o que faz a leitura falhar.
      shapeRendering="crispEdges"
      data-testid="qr-code"
      data-modulos={modulos}
    >
      {/* Fundo branco EXPLÍCITO, e não herdado: o QR precisa de contraste máximo,
          e um dia alguém coloca esta tela sobre fundo escuro. */}
      <rect width={lado} height={lado} fill="#ffffff" />
      {escuros.map(([linha, coluna]) => (
        <rect
          key={`${linha}-${coluna}`}
          x={coluna + margem}
          y={linha + margem}
          width={1}
          height={1}
          fill="#000000"
        />
      ))}
    </svg>
  );
}
